import type { PriorityPaymentsQuery } from '@/Network/Types/Requests/PriorityPaymentsRequests'
import type {
  PriorityCheckStatus,
  PriorityOrderBy,
  PrioritySituation,
  PrioritySlaBand,
  PrioritySlaState,
  PriorityTab
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

export type PriorityPeriodPreset = 'CURRENT' | 'PREVIOUS' | 'LAST_7_DAYS' | 'CUSTOM'

export const PRIORITY_PERIOD_PRESETS: { value: PriorityPeriodPreset; label: string }[] = [
  { value: 'CURRENT', label: 'Ciclo atual' },
  { value: 'PREVIOUS', label: 'Ciclo anterior' },
  { value: 'LAST_7_DAYS', label: 'Últimos 7 dias' },
  { value: 'CUSTOM', label: 'Personalizado' }
]

/** Atalho "Parado há +24 h". */
export const PRIORITY_STALLED_HOURS = 24
/** Atalho "2+ rejeições". */
export const PRIORITY_MIN_REJECTIONS = 2
export const PRIORITY_PAGE_LIMIT = 25

/** Ordem do M3 (atalhos, seletor e agrupamento). */
export const PRIORITY_SLA_STATES = [
  'OVERDUE',
  'URGENT',
  'ATTENTION',
  'ON_TIME',
  'DONE',
  'NOT_STARTED'
] as const satisfies readonly PrioritySlaState[]

/** Aba ↔ valor da URL (`aba`). O padrão (`ALL` ↔ `todos`) não vai para a URL. */
export const PRIORITY_TAB_URL_VALUES: Record<PriorityTab, string> = {
  ALL: 'todos',
  IN_CASH: 'a-vista',
  ANTECIPE: 'antecipe'
}

const ALL_SITUATIONS = [
  'FUTURE',
  'IN_PROGRESS',
  'RESOLVED_APP',
  'RESOLVED_SHEET',
  'RESOLVED_OTHER',
  'APPROVED',
  'AWAITING_AUDIT',
  'REJECTED',
  'NOT_SENT',
  'NOT_REQUIRED'
] as const satisfies readonly PrioritySituation[]
const ALL_CHECKS = [
  'NONE',
  'PARTIAL',
  'PENDING',
  'OK'
] as const satisfies readonly PriorityCheckStatus[]
const ALL_ORDERS = [
  'PRIORITY',
  'LAST_ACTIVITY',
  'STATION_START'
] as const satisfies readonly PriorityOrderBy[]
const ALL_BANDS = [
  'IN_CASH_SEND',
  'IN_CASH_AUDIT',
  'ANTECIPE'
] as const satisfies readonly PrioritySlaBand[]
const ALL_TABS = Object.keys(PRIORITY_TAB_URL_VALUES) as PriorityTab[]

const VALID_SITUATIONS = new Set<string>(ALL_SITUATIONS)
const VALID_CHECK = new Set<string>(ALL_CHECKS)
const VALID_ORDER = new Set<string>(ALL_ORDERS)
const VALID_PRESETS = new Set<string>(PRIORITY_PERIOD_PRESETS.map((p) => p.value))
const VALID_SLA_STATES = new Set<string>(PRIORITY_SLA_STATES)
const VALID_BANDS = new Set<string>(ALL_BANDS)
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** A linha do relógio de um tipo só faz sentido na aba Todos ou na aba desse tipo. */
const BAND_TAB: Record<PrioritySlaBand, PriorityTab> = {
  IN_CASH_SEND: 'IN_CASH',
  IN_CASH_AUDIT: 'IN_CASH',
  ANTECIPE: 'ANTECIPE'
}

export interface PriorityPaymentsFilters {
  preset: PriorityPeriodPreset
  /** Só usados com `LAST_7_DAYS` e `CUSTOM`. */
  startAt: string | null
  endAt: string | null
  /** Aba (Q9). Define o SLA principal da linha (spec §3.4). */
  tab: PriorityTab
  situations: PrioritySituation[]
  operationalGroups: string[]
  healthUnits: number[]
  schedules: number[]
  /** Estado do SLA principal (multi, inclui `NOT_STARTED`). */
  slaStates: PrioritySlaState[]
  /** Linha do relógio clicada. Só no Ciclo atual. */
  slaBand: PrioritySlaBand | null
  doctorIds: number[]
  /** Data do plantão (`s.date`), `'YYYY-MM-DD'`, inclusiva. */
  stationDateFrom: string | null
  stationDateTo: string | null
  checkStatus: PriorityCheckStatus | null
  search: string
  /** Legado (links antigos `critical=true`); crítico = só 2+ rejeições (A3). */
  onlyCritical: boolean
  onlyOverdue: boolean
  minRejections: number | null
  doctorWithoutUser: boolean
  stalledHours: number | null
  orderBy: PriorityOrderBy
}

export const DEFAULT_PRIORITY_FILTERS: PriorityPaymentsFilters = {
  preset: 'CURRENT',
  startAt: null,
  endAt: null,
  tab: 'ALL',
  situations: [],
  operationalGroups: [],
  healthUnits: [],
  schedules: [],
  slaStates: [],
  slaBand: null,
  doctorIds: [],
  stationDateFrom: null,
  stationDateTo: null,
  checkStatus: null,
  search: '',
  onlyCritical: false,
  onlyOverdue: false,
  minRejections: null,
  doctorWithoutUser: false,
  stalledHours: null,
  orderBy: 'PRIORITY'
}

/** Janela do atalho "Últimos 7 dias": sete dias contados do instante atual. */
export const lastSevenDaysRange = (now: Date): { startAt: string; endAt: string } => ({
  startAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  endAt: now.toISOString()
})

const bandFits = (filters: PriorityPaymentsFilters, band: PrioritySlaBand): boolean =>
  filters.preset === 'CURRENT' && (filters.tab === 'ALL' || BAND_TAB[band] === filters.tab)

const stationDatesSwapped = ({
  stationDateFrom: from,
  stationDateTo: to
}: PriorityPaymentsFilters) => from !== null && to !== null && from > to

/**
 * Recorte coerente, aplicado na hidratação e em todo `setFilters`:
 * - `slaBand` só no Ciclo atual (o BE o ignora fora dele e a tela mentiria o
 *   selo "Só: …") e só na aba Todos ou na do próprio tipo (M3);
 * - `onlyOverdue` ("Atrasados") só no Ciclo atual, pela mesma regra: o atalho
 *   mora nos contadores do SLA, que só aparecem no Ciclo atual — fora dele o
 *   filtro ficaria ligado e invisível;
 * - `stationDateFrom > stationDateTo` seria 400 no BE: as datas são trocadas.
 * Devolve o MESMO objeto quando nada muda (evita reatividade à toa).
 */
export const normalizePriorityFilters = (
  filters: PriorityPaymentsFilters
): PriorityPaymentsFilters => {
  const slaBand = filters.slaBand && bandFits(filters, filters.slaBand) ? filters.slaBand : null
  const onlyOverdue = filters.onlyOverdue && filters.preset === 'CURRENT'
  const swap = stationDatesSwapped(filters)
  if (slaBand === filters.slaBand && onlyOverdue === filters.onlyOverdue && !swap) return filters
  return {
    ...filters,
    slaBand,
    onlyOverdue,
    stationDateFrom: swap ? filters.stationDateTo : filters.stationDateFrom,
    stationDateTo: swap ? filters.stationDateFrom : filters.stationDateTo
  }
}

/**
 * `cycle` e `startAt/endAt` são mutuamente exclusivos no contrato: mandar os
 * dois deixaria o backend escolher, e a tela mostraria um período que não é o
 * que o botão diz.
 */
const periodToQuery = (filters: PriorityPaymentsFilters): PriorityPaymentsQuery => {
  if (filters.preset === 'CURRENT' || filters.preset === 'PREVIOUS') {
    return { cycle: filters.preset }
  }
  const query: PriorityPaymentsQuery = {}
  if (filters.startAt) query.startAt = filters.startAt
  if (filters.endAt) query.endAt = filters.endAt
  return query
}

const listsToQuery = (filters: PriorityPaymentsFilters): PriorityPaymentsQuery => {
  const query: PriorityPaymentsQuery = {}
  if (filters.situations.length) query.situations = [...filters.situations]
  if (filters.operationalGroups.length) query.operationalGroups = [...filters.operationalGroups]
  if (filters.healthUnits.length) query.healthUnits = [...filters.healthUnits]
  if (filters.schedules.length) query.schedules = [...filters.schedules]
  return query
}

/** O web novo NÃO manda `types` (DO-3); `tab` só quando ≠ `ALL`, o padrão do BE. */
const slaToQuery = (filters: PriorityPaymentsFilters): PriorityPaymentsQuery => {
  const query: PriorityPaymentsQuery = {}
  if (filters.tab !== 'ALL') query.tab = filters.tab
  if (filters.slaStates.length) query.slaState = [...filters.slaStates]
  if (filters.slaBand) query.slaBand = filters.slaBand
  if (filters.doctorIds.length) query.doctorIds = [...filters.doctorIds]
  if (filters.stationDateFrom) query.stationDateFrom = filters.stationDateFrom
  if (filters.stationDateTo) query.stationDateTo = filters.stationDateTo
  return query
}

const narrowingToQuery = (filters: PriorityPaymentsFilters): PriorityPaymentsQuery => {
  const query: PriorityPaymentsQuery = {}
  if (filters.checkStatus) query.checkStatus = filters.checkStatus
  if (filters.search.trim()) query.search = filters.search.trim()
  if (filters.onlyCritical) query.onlyCritical = true
  if (filters.onlyOverdue) query.onlyOverdue = true
  if (filters.minRejections !== null) query.minRejections = filters.minRejections
  if (filters.doctorWithoutUser) query.doctorWithoutUser = true
  if (filters.stalledHours !== null) query.stalledHours = filters.stalledHours
  return query
}

export const priorityFiltersToQuery = (
  filters: PriorityPaymentsFilters,
  page: number
): PriorityPaymentsQuery => ({
  orderBy: filters.orderBy,
  page,
  limit: PRIORITY_PAGE_LIMIT,
  ...periodToQuery(filters),
  ...listsToQuery(filters),
  ...slaToQuery(filters),
  ...narrowingToQuery(filters)
})

const csv = (values: (string | number)[]): string => values.join(',')

/**
 * Só o que difere do padrão entra na URL: a fila compartilhada por link mostra
 * o recorte, não a configuração inteira. Chaves curtas (`q`, `units`, `aba`,
 * `sla`, `prazo`) porque a URL é lida e colada por gente.
 *
 * Datas do período só vão com `CUSTOM`: `LAST_7_DAYS` é relativo a quem abre o
 * link e é recalculado na hidratação. Grupos operacionais vão como chave
 * repetida (`string[]`) porque o nome é texto livre e pode conter vírgula.
 */
type PriorityFiltersUrl = Record<string, string | string[]>

const periodToUrl = (filters: PriorityPaymentsFilters): PriorityFiltersUrl => {
  if (filters.preset === 'CURRENT') return {}
  const url: PriorityFiltersUrl = { preset: filters.preset }
  if (filters.preset === 'CUSTOM') {
    if (filters.startAt) url.startAt = filters.startAt
    if (filters.endAt) url.endAt = filters.endAt
  }
  return url
}

const listsToUrl = (filters: PriorityPaymentsFilters): PriorityFiltersUrl => {
  const url: PriorityFiltersUrl = {}
  if (filters.situations.length) url.situations = csv(filters.situations)
  if (filters.operationalGroups.length) url.groups = [...filters.operationalGroups]
  if (filters.healthUnits.length) url.units = csv(filters.healthUnits)
  if (filters.schedules.length) url.schedules = csv(filters.schedules)
  return url
}

const slaToUrl = (filters: PriorityPaymentsFilters): PriorityFiltersUrl => {
  const url: PriorityFiltersUrl = {}
  if (filters.tab !== 'ALL') url.aba = PRIORITY_TAB_URL_VALUES[filters.tab]
  if (filters.slaStates.length) url.sla = csv(filters.slaStates)
  if (filters.slaBand) url.prazo = filters.slaBand
  if (filters.doctorIds.length) url.medicos = csv(filters.doctorIds)
  if (filters.stationDateFrom) url.dataDe = filters.stationDateFrom
  if (filters.stationDateTo) url.dataAte = filters.stationDateTo
  return url
}

const narrowingToUrl = (filters: PriorityPaymentsFilters): PriorityFiltersUrl => {
  const url: PriorityFiltersUrl = {}
  if (filters.checkStatus) url.check = filters.checkStatus
  if (filters.search.trim()) url.q = filters.search.trim()
  if (filters.onlyCritical) url.critical = 'true'
  if (filters.onlyOverdue) url.overdue = 'true'
  if (filters.minRejections !== null) url.rej = String(filters.minRejections)
  if (filters.doctorWithoutUser) url.nouser = 'true'
  if (filters.stalledHours !== null) url.stalled = String(filters.stalledHours)
  return url
}

export const priorityFiltersToUrl = (
  filters: PriorityPaymentsFilters,
  page: number
): PriorityFiltersUrl => {
  const url: PriorityFiltersUrl = {
    ...periodToUrl(filters),
    ...listsToUrl(filters),
    ...slaToUrl(filters),
    ...narrowingToUrl(filters)
  }
  if (filters.orderBy !== 'PRIORITY') url.orderBy = filters.orderBy
  if (page > 1) url.page = String(page)
  return url
}

const readString = (value: unknown): string => (typeof value === 'string' ? value : '')

const readList = (value: unknown): string[] =>
  readString(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)

/** Chave repetida (`string[]`) ou valor único; cada item é inteiro, sem split. */
const readRepeated = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)

const isPositiveInt = (n: number): boolean => Number.isInteger(n) && n > 0

const readIds = (value: unknown): number[] => readList(value).map(Number).filter(isPositiveInt)

const readPositiveInt = (value: unknown, fallback: number | null): number | null => {
  const n = Number(readString(value))
  return isPositiveInt(n) ? n : fallback
}

const readDate = (value: unknown): string | null => {
  const raw = readString(value)
  return raw && Number.isFinite(Date.parse(raw)) ? raw : null
}

/** `'YYYY-MM-DD'` de calendário válido (`2026-02-30` cai fora). */
const readDay = (value: unknown): string | null => {
  const raw = readString(value)
  if (!ISO_DAY.test(raw)) return null
  const parsed = new Date(`${raw}T12:00:00Z`)
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === raw ? raw : null
}

/**
 * `aba` manda. Sem ela, um link antigo com `types=IN_CASH` (ou só `ANTECIPE`)
 * abre na aba do tipo. Busca por `find`, e não por índice de objeto: `aba=constructor`
 * não pode virar uma aba.
 */
const readTab = (query: Record<string, unknown>): PriorityTab => {
  const aba = readString(query.aba)
  const fromAba = ALL_TABS.find((tab) => PRIORITY_TAB_URL_VALUES[tab] === aba)
  if (fromAba) return fromAba
  const legacy = readList(query.types)
  if (legacy.length === 1 && (legacy[0] === 'IN_CASH' || legacy[0] === 'ANTECIPE')) return legacy[0]
  return 'ALL'
}

const readSla = (
  query: Record<string, unknown>
): Pick<
  PriorityPaymentsFilters,
  'tab' | 'slaStates' | 'slaBand' | 'doctorIds' | 'stationDateFrom' | 'stationDateTo'
> => {
  const band = readString(query.prazo)
  const states = readList(query.sla).filter((s): s is PrioritySlaState => VALID_SLA_STATES.has(s))
  return {
    tab: readTab(query),
    slaStates: [...new Set(states)],
    slaBand: VALID_BANDS.has(band) ? (band as PrioritySlaBand) : null,
    doctorIds: [...new Set(readIds(query.medicos))],
    stationDateFrom: readDay(query.dataDe),
    stationDateTo: readDay(query.dataAte)
  }
}

/**
 * Período coerente: `CUSTOM` só com as duas datas válidas (senão volta ao
 * ciclo atual); `LAST_7_DAYS` recalculado a partir de agora; ciclos sem datas.
 */
const readPeriod = (
  query: Record<string, unknown>
): Pick<PriorityPaymentsFilters, 'preset' | 'startAt' | 'endAt'> => {
  const preset = readString(query.preset)
  const current = { preset: 'CURRENT' as const, startAt: null, endAt: null }
  if (!VALID_PRESETS.has(preset)) return current

  if (preset === 'CUSTOM') {
    const startAt = readDate(query.startAt)
    const endAt = readDate(query.endAt)
    if (startAt && endAt) return { preset, startAt, endAt }
  }
  if (preset === 'LAST_7_DAYS') return { preset, ...lastSevenDaysRange(new Date()) }
  if (preset === 'PREVIOUS') return { preset, startAt: null, endAt: null }

  return current
}

const readNarrowing = (
  query: Record<string, unknown>
): Pick<
  PriorityPaymentsFilters,
  | 'checkStatus'
  | 'search'
  | 'onlyCritical'
  | 'onlyOverdue'
  | 'minRejections'
  | 'doctorWithoutUser'
  | 'stalledHours'
  | 'orderBy'
> => {
  const check = readString(query.check)
  const orderBy = readString(query.orderBy)
  return {
    checkStatus: VALID_CHECK.has(check) ? (check as PriorityCheckStatus) : null,
    search: readString(query.q),
    onlyCritical: readString(query.critical) === 'true',
    // A2 (25/09): `antecipeSemHorario` de links antigos é ignorado — o chip virou export.
    onlyOverdue: readString(query.overdue) === 'true',
    minRejections: readPositiveInt(query.rej, null),
    doctorWithoutUser: readString(query.nouser) === 'true',
    stalledHours: readPositiveInt(query.stalled, null),
    orderBy: VALID_ORDER.has(orderBy) ? (orderBy as PriorityOrderBy) : 'PRIORITY'
  }
}

/**
 * URL → filtros, tolerante: valor desconhecido cai no padrão em vez de
 * derrubar a tela. Um link antigo (ou editado à mão) precisa abrir a fila, nem
 * que seja no recorte de sempre.
 */
export const priorityFiltersFromUrl = (
  query: Record<string, unknown>
): { filters: PriorityPaymentsFilters; page: number } => ({
  filters: normalizePriorityFilters({
    ...readPeriod(query),
    ...readSla(query),
    situations: readList(query.situations).filter((s): s is PrioritySituation =>
      VALID_SITUATIONS.has(s)
    ),
    operationalGroups: readRepeated(query.groups),
    healthUnits: readIds(query.units),
    schedules: readIds(query.schedules),
    ...readNarrowing(query)
  }),
  page: readPositiveInt(query.page, 1) ?? 1
})

/**
 * Alimenta o estado vazio ("Remova um atalho ou amplie o período"). Ordenação
 * e aba não filtram no sentido da tela (a aba é navegação e o "Limpar filtros"
 * a preserva), então não contam.
 */
export const hasActivePriorityFilters = (filters: PriorityPaymentsFilters): boolean =>
  Object.keys(
    priorityFiltersToUrl(
      {
        ...filters,
        tab: DEFAULT_PRIORITY_FILTERS.tab,
        orderBy: DEFAULT_PRIORITY_FILTERS.orderBy
      },
      1
    )
  ).length > 0
